import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MailIcon, LockIcon, LogInIcon, LoaderIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
interface LoginScreenProps {
  onLogin: (token: string) => void;
}
export function LoginScreen({
  onLogin
}: LoginScreenProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('이메일을 입력해주세요');
      return;
    }
    if (!password.trim()) {
      setError('비밀번호를 입력해주세요');
      return;
    }
    setIsLoading(true);
    // Mock ThingsBoard login - simulate API call
    // In production: POST /api/auth/login { username: email, password }
    try {
      await new Promise(resolve => setTimeout(resolve, 1200));
      // Mock validation
      if (email.includes('@') && password.length >= 4) {
        const mockToken = 'eyJhbGciOiJIUzI1NiJ9.mock-thingsboard-jwt-token';
        onLogin(mockToken);
      } else {
        setError('이메일 또는 비밀번호가 올바르지 않습니다');
      }
    } catch {
      setError('서버 연결에 실패했습니다');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-[#0a0f1a] flex flex-col items-center justify-center px-6" data-id="element-281">
      {/* Background subtle grid */}
      <div className="fixed inset-0 pointer-events-none opacity-10" style={{
      backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
      backgroundSize: '32px 32px'
    }} data-id="element-282" />
      <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(circle_at_center,_transparent_0%,_#0a0f1a_100%)]" data-id="element-283" />

      <motion.div initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="relative z-10 w-full max-w-sm" data-id="element-284">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10" data-id="element-285">
          <motion.img src="https://cdn.magicpatterns.com/uploads/75vRfXbVKD5kLqaUpQiaw4/remove_background_fr.png" alt="Samsung" className="w-36 h-auto mb-4 opacity-70" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 0.7,
          scale: 1
        }} transition={{
          delay: 0.2,
          duration: 0.4
        }} data-id="element-286" />
          <motion.p className="text-xs text-slate-500 font-mono tracking-widest uppercase" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.4
        }} data-id="element-287">
            Smart Home IoT
          </motion.p>
        </div>

        {/* Login Card */}
        <motion.form onSubmit={handleSubmit} className="glass-panel rounded-2xl p-6 space-y-5" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3,
        duration: 0.4
      }} data-id="element-288">
          <div className="text-center mb-2" data-id="element-289">
            <h1 className="text-lg font-bold text-slate-200" data-id="element-290">로그인</h1>
            <p className="text-xs text-slate-500 mt-1" data-id="element-291">
              ThingsBoard 계정으로 로그인
            </p>
          </div>

          {/* Email */}
          <div className="space-y-1.5" data-id="element-292">
            <label className="text-xs text-slate-400 font-medium pl-1" data-id="element-293">
              이메일
            </label>
            <div className="relative" data-id="element-294">
              <MailIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-500" data-id="element-295" />
              <input type="email" value={email} onChange={e => {
              setEmail(e.target.value);
              setError('');
            }} placeholder="user@example.com" className="w-full h-12 pl-11 pr-4 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-slate-600 focus:border-cyan-500/50 focus:outline-none focus:ring-1 focus:ring-cyan-500/30 transition-all" autoComplete="email" autoCapitalize="none" data-id="element-296" />
            </div>
          </div>

          {/* Password */}
          <div className="space-y-1.5" data-id="element-297">
            <label className="text-xs text-slate-400 font-medium pl-1" data-id="element-298">
              비밀번호
            </label>
            <div className="relative" data-id="element-299">
              <LockIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-500" data-id="element-300" />
              <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => {
              setPassword(e.target.value);
              setError('');
            }} placeholder="••••••••" className="w-full h-12 pl-11 pr-12 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-slate-600 focus:border-cyan-500/50 focus:outline-none focus:ring-1 focus:ring-cyan-500/30 transition-all" autoComplete="current-password" data-id="element-301" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-slate-300 transition-colors" data-id="element-302">
                {showPassword ? <EyeOffIcon className="w-4 h-4" data-id="element-303" /> : <EyeIcon className="w-4 h-4" data-id="element-304" />}
              </button>
            </div>
          </div>

          {/* Error */}
          {error && <motion.div initial={{
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-xs text-red-400 text-center py-1 font-medium" data-id="element-305">
              {error}
            </motion.div>}

          {/* Submit */}
          <button type="submit" disabled={isLoading} className="w-full h-12 rounded-xl bg-cyan-600 hover:bg-cyan-500 active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100 text-white text-sm font-bold flex items-center justify-center gap-2 transition-all" data-id="element-306">
            {isLoading ? <LoaderIcon className="w-4.5 h-4.5 animate-spin" data-id="element-307" /> : <>
                <LogInIcon className="w-4 h-4" data-id="element-308" />
                로그인
              </>}
          </button>
        </motion.form>

        {/* Footer */}
        <motion.p className="text-center text-[11px] text-slate-600 mt-6 font-mono" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.6
      }} data-id="element-309">
          SeqSys IoT Platform v2.0
        </motion.p>
      </motion.div>
    </div>;
}